import React from 'react';

export type Transition = "reset" | undefined;

export type InputRefType = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | undefined;
export type InputRef = React.MutableRefObject<InputRefType> | undefined

export interface IField {
    name: string
    setName: (fieldName: string) => void

    value: any
    setValue: (value: any) => void
    clear: VoidFunction
    reset: VoidFunction

    isEmpty: () => boolean

    error: string | undefined
    setError: (value?: string) => void
    clearError: VoidFunction

    initialValueRef: React.MutableRefObject<any> | undefined
    inputValueRef: React.MutableRefObject<any> | undefined

    transition: Transition
    setTransition: (transition: Transition) => void
    clearTransition: VoidFunction

    getInputValue: () => any
    setInputValue: (value: any) => void
}

export function useField<TValue = any>(initialValue?: TValue, fieldName: string = "") {
    const [_name, _setName] = React.useState(fieldName);
    const [_value, _setValue] = React.useState(initialValue);
    const [_error, _setError] = React.useState<string>();
    const [_transition, _setTransition] = React.useState<Transition>();

    const initialValueRef = React.useRef(initialValue);
    const inputValueRef = React.useRef(initialValue);

    const setName = React.useCallback((fieldName: string) => {
        _setName(fieldName);
    }, []);

    const setValue = React.useCallback((newValue: any) => {
        if (newValue !== _value) {
            _setValue(newValue);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldName, _value]);

    const setError = React.useCallback((error?: string) => {
        _setError(error);
    }, []);

    const clearError = React.useCallback(() => {
        _setError(undefined);
    }, []);

    const getInputValue = React.useCallback(() => {
        return inputValueRef.current;
    }, []);

    const setInputValue = React.useCallback((value: any) => {
        inputValueRef.current = value;
    }, []);

    const clear = React.useCallback(() => {
        setValue(undefined);
        setInputValue(undefined);
    }, [setInputValue, setValue]);

    const setTransition = React.useCallback((transition: Transition) => {
        _setTransition(transition);
    }, []);

    const clearTransition = React.useCallback(() => {
        _setTransition(undefined);
    }, []);

    const reset = React.useCallback(() => {
        setValue(initialValue);
        setInputValue(initialValue);
        clearError();
        setTransition("reset");
    }, [clearError, initialValue, setInputValue, setTransition, setValue]);

    const _isEmpty = React.useCallback(() => {
        return (
            _value === undefined || 
            _value === null || 
            (typeof(_value) === "string" && _value === "") ||
            (Array.isArray(_value) && _value.length === 0) || 
            (typeof(_value) === 'object' && Object.keys(_value || {}).length === 0)
        );
    }, [_value]);

    return React.useMemo(() => ({
        name: _name,
        setName,

        value: _value,
        setValue,
        clear,
        reset,

        isEmpty: _isEmpty,

        error: _error,
        setError,
        clearError,
        
        inputValueRef,
        initialValueRef,
        transition: _transition,
        setTransition,
        clearTransition,

        getInputValue,
        setInputValue

    }), [_name, setName, _value, setValue, clear, reset, _isEmpty, _error, setError, clearError, _transition, setTransition, clearTransition, getInputValue, setInputValue]);
}
